if (!customElements.get('product-details')) {
  customElements.define(
    'product-details',
    class ProductDetails extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.dataset.initialized = 'true';
        this.variants = this.readVariants();
        this.addEventListener('click', this.handleClick.bind(this));
        this.addEventListener('change', this.handleChange.bind(this));

        const productForm = this.querySelector('[data-product-purchase] form');
        if (productForm) this.updateVariant(productForm, false);
      }

      readVariants() {
        const variantsElement = this.querySelector('[data-product-variants]');
        if (!variantsElement) return [];

        try {
          return JSON.parse(variantsElement.textContent);
        } catch (error) {
          console.error('Unable to parse product variants.', error);
          return [];
        }
      }

      handleClick(event) {
        const scrollButton = event.target.closest('[data-thumbnail-scroll]');
        if (scrollButton) {
          this.scrollThumbnails(Number(scrollButton.dataset.thumbnailScroll));
          return;
        }

        const thumbnail = event.target.closest('[data-product-thumbnail]');
        if (thumbnail) {
          this.changeMainImage(thumbnail, thumbnail.dataset.imageUrl);
          return;
        }

        const quantityButton = event.target.closest('[data-quantity-change]');
        if (quantityButton) {
          this.updateQuantity(quantityButton, Number(quantityButton.dataset.quantityChange));
        }
      }

      handleChange(event) {
        const variantInput = event.target.closest('[data-variant-input]');
        if (!variantInput) return;

        const option = variantInput.closest('[data-variant-option]');
        const selectedValue = option?.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.textContent = variantInput.value;

        const selectedSwatch = variantInput.closest('label')?.querySelector('[data-color-swatch-frame]');
        if (selectedSwatch && option) {
          option.querySelectorAll('[data-color-swatch-frame]').forEach((swatch) => {
            swatch.classList.remove('border-primary', 'scale-110');
            swatch.classList.add('border-gray-200');
          });
          selectedSwatch.classList.remove('border-gray-200');
          selectedSwatch.classList.add('border-primary', 'scale-110');
        }

        const productForm = variantInput.closest('form');
        if (productForm) this.updateVariant(productForm, true);
      }

      updateVariant(productForm, updateUrl) {
        const selectedOptions = Array.from(productForm.querySelectorAll('[data-variant-input]:checked')).map(
          (input) => input.value,
        );

        let matchedVariant;
        if (selectedOptions.length === 0) {
          const currentVariantId = Number(productForm.querySelector('[data-variant-id]')?.value);
          matchedVariant = this.variants.find((variant) => variant.id === currentVariantId) || this.variants[0];
        } else {
          matchedVariant = this.variants.find((variant) =>
            variant.options.every((option, index) => option === selectedOptions[index]),
          );
        }

        if (!matchedVariant) return;

        if (updateUrl) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('variant', matchedVariant.id);
          window.history.replaceState({}, '', newUrl);
        }

        this.querySelectorAll('[data-variant-id]').forEach((input) => {
          input.value = matchedVariant.id;
        });

        this.updatePrice(matchedVariant);
        this.updateAvailability(matchedVariant);
        this.updateFeaturedImage(matchedVariant);
      }

      updatePrice(variant) {
        this.querySelectorAll('[data-product-price]').forEach((price) => {
          price.textContent = this.formatMoney(variant.price);
        });

        const hasDiscount = variant.compare_at_price > variant.price;
        this.querySelectorAll('[data-compare-price]').forEach((comparePrice) => {
          comparePrice.classList.toggle('hidden', !hasDiscount);
          comparePrice.textContent = hasDiscount ? this.formatMoney(variant.compare_at_price) : '';
        });

        this.querySelectorAll('[data-discount-badge]').forEach((badge) => {
          badge.classList.toggle('hidden', !hasDiscount);
          badge.textContent = hasDiscount
            ? `Save ${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%`
            : '';
        });
      }

      updateAvailability(variant) {
        this.querySelectorAll('[data-product-purchase]').forEach((purchaseBlock) => {
          const addButton = purchaseBlock.querySelector('[data-add-button]');
          const addButtonText = purchaseBlock.querySelector('[data-add-button-text]');
          const dynamicCheckout = purchaseBlock.querySelector('[data-dynamic-checkout]');

          if (addButton) addButton.disabled = !variant.available;
          if (addButtonText) addButtonText.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
          if (dynamicCheckout) dynamicCheckout.classList.toggle('hidden', !variant.available);
        });
      }

      updateFeaturedImage(variant) {
        if (!variant.featured_image) return;

        const thumbnail = this.querySelector(
          `[data-product-thumbnail][data-image-id="${variant.featured_image.id}"]`,
        );
        if (thumbnail) {
          this.changeMainImage(thumbnail, thumbnail.dataset.imageUrl);
        } else if (variant.featured_image.src) {
          this.changeMainImage(null, variant.featured_image.src);
        }
      }

      scrollThumbnails(direction) {
        const container = this.querySelector('[data-product-thumbnails]');
        if (!container) return;

        const isDesktop = window.innerWidth >= 768;
        if (isDesktop) {
          container.scrollBy({
            top: direction * Math.max(container.clientHeight - 80, 80),
            behavior: 'smooth',
          });
        } else {
          container.scrollBy({
            left: direction * Math.max(container.clientWidth - 64, 64),
            behavior: 'smooth',
          });
        }
      }

      changeMainImage(thumbnail, source) {
        const mainImage = this.querySelector('[data-main-product-image]');
        if (!mainImage || !source) return;

        mainImage.style.opacity = '0';
        window.setTimeout(() => {
          mainImage.src = source;
          mainImage.style.opacity = '1';
        }, 150);

        this.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
          button.classList.remove('border-primary');
          button.classList.add('border-transparent');
        });
        if (thumbnail) {
          thumbnail.classList.remove('border-transparent');
          thumbnail.classList.add('border-primary');
        }
      }

      updateQuantity(button, change) {
        const purchaseBlock = button.closest('[data-product-purchase]');
        const input = purchaseBlock?.querySelector('[data-quantity-input]');
        if (!input) return;

        const currentValue = Number.parseInt(input.value, 10) || 1;
        input.value = Math.max(1, currentValue + change);
      }

      formatMoney(cents) {
        const formatted = new Intl.NumberFormat('en-IN', {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        }).format(cents / 100);
        return `₹${formatted}`;
      }
    },
  );
}
